import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, Send, Loader2, Lock, Sparkles, Mic, Square, Volume2, UserCheck } from "lucide-react";

const md = (t) => t.split(/(\*\*[^*]+\*\*)/g).map((p, i) => p.startsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : p);

export const TaxPalChat = ({ year, hasPlan }) => {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [speaking, setSpeaking] = useState(null);
  const [handing, setHanding] = useState(null);
  const recRef = useRef(null);
  const audioRef = useRef(null);
  const endRef = useRef(null);
  const canRecord = typeof window !== "undefined" && !!navigator.mediaDevices && !!window.MediaRecorder;

  const playAudio = async (audioId, chatId) => {
    try {
      setSpeaking(chatId);
      const { data } = await api.get(`/vault/taxpal/audio/${audioId}.mp3`, { responseType: "blob" });
      if (audioRef.current) audioRef.current.pause();
      const a = new Audio(URL.createObjectURL(data));
      audioRef.current = a;
      a.onended = () => setSpeaking(null);
      await a.play();
    } catch { setSpeaking(null); toast.error("Couldn't play audio"); }
  };

  const speak = async (chatId) => {
    if (speaking === chatId) { audioRef.current?.pause(); setSpeaking(null); return; }
    try {
      setSpeaking(chatId);
      const { data } = await api.post(`/vault/taxpal/speak/${chatId}`);
      await playAudio(data.audio_id, chatId);
    } catch (e) { setSpeaking(null); toast.error(e.response?.data?.detail || "Couldn't generate speech"); }
  };

  const handoff = async (chatId) => {
    setHanding(chatId);
    try {
      await api.post("/vault/taxpal/handoff", { chat_id: chatId });
      toast.success("Sent to your tax team with the full context — they'll reply in your case messages");
      setState((s) => ({ ...s, messages: s.messages.map((m) => (m.chat_id === chatId ? { ...m, handed_off_at: new Date().toISOString() } : m)) }));
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send to your tax team"); } finally { setHanding(null); }
  };

  const toggleRecord = async () => {
    if (recording) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: mime });
        if (blob.size < 1000) { toast.error("That was too short — hold the mic and ask your question"); return; }
        setOpen(true); setBusy(true);
        setState((s) => ({ ...s, messages: [...(s?.messages || []), { role: "user", content: "🎤 …", chat_id: `tmp-${Date.now()}`, pending: true }] }));
        try {
          const fd = new FormData(); fd.append("file", blob, mime === "audio/webm" ? "q.webm" : "q.mp4");
          const { data } = await api.post("/vault/taxpal/voice", fd, { params: { year }, headers: { "Content-Type": "multipart/form-data" } });
          setState((s) => ({ ...s, remaining: data.remaining, messages: [...s.messages.filter((m) => !m.pending), { role: "user", content: data.question, chat_id: `q-${Date.now()}` }, { role: "assistant", content: data.reply, chat_id: data.chat_id, audio_id: data.audio_id }] }));
          if (data.audio_id) playAudio(data.audio_id, data.chat_id);
        } catch (e) {
          setState((s) => ({ ...s, messages: s.messages.filter((m) => !m.pending) }));
          toast.error(e.response?.data?.detail || "Voice question failed");
          if (e.response?.status === 403) setState((s) => ({ ...s, remaining: 0 }));
        } finally { setBusy(false); }
      };
      recRef.current = rec; rec.start(); setRecording(true);
      setTimeout(() => rec.state === "recording" && rec.stop(), 30000);
    } catch { toast.error("Microphone access is needed for voice questions"); }
  };

  useEffect(() => { api.get("/vault/taxpal/chat").then((r) => setState(r.data)).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [state?.messages?.length, busy]);

  const ask = async (q) => {
    const text = (q || input).trim();
    if (!text || busy) return;
    setOpen(true); setBusy(true); setInput("");
    setState((s) => ({ ...s, messages: [...(s?.messages || []), { role: "user", content: text, chat_id: `tmp-${Date.now()}` }] }));
    try {
      const { data } = await api.post("/vault/taxpal/chat", { message: text }, { params: { year } });
      setState((s) => ({ ...s, remaining: data.remaining, messages: [...s.messages, { role: "assistant", content: data.reply, chat_id: data.chat_id }] }));
    } catch (e) {
      const msg = e.response?.data?.detail || "Tax Pal couldn't answer just now";
      toast.error(msg);
      if (e.response?.status === 403) setState((s) => ({ ...s, remaining: 0 }));
    } finally { setBusy(false); }
  };

  if (!state) return null;
  const exhausted = !state.unlimited && state.remaining === 0;
  const msgs = state.messages || [];

  return (
    <div className="mt-5 border border-white/15 rounded-2xl overflow-hidden" data-testid="taxpal-chat">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-white/5" data-testid="taxpal-chat-toggle">
        <span className="flex items-center gap-2 text-sm font-semibold"><MessageCircle className="h-4 w-4 text-[#7FE3AE]" /> Ask Tax Pal anything about your numbers</span>
        <span className="text-[11px] text-[#A7B4CC]" data-testid="taxpal-chat-remaining">{state.unlimited ? "Unlimited" : `${state.remaining} of ${state.limit} free this month`}</span>
      </button>

      {(open || msgs.length > 0) && (
        <div className="px-4 pb-4">
          {msgs.length === 0 && (
            <div className="flex flex-wrap gap-2 mb-3" data-testid="taxpal-chat-suggestions">
              {state.suggestions.map((s) => (
                <button key={s} onClick={() => ask(s)} disabled={exhausted} data-testid="taxpal-chat-suggestion"
                  className="text-xs px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-40">{s}</button>
              ))}
            </div>
          )}
          {msgs.length > 0 && (
            <div className="max-h-80 overflow-y-auto space-y-2 pr-1 mb-3" data-testid="taxpal-chat-messages">
              {msgs.map((m) => (
                <div key={m.chat_id} className={`max-w-[92%] ${m.role === "user" ? "ml-auto" : ""}`}>
                  <div className={`text-sm leading-relaxed rounded-2xl px-3.5 py-2.5 whitespace-pre-wrap ${m.role === "user" ? "bg-[#7FE3AE] text-[#02112A]" : "bg-white/10 text-white"}`}
                    data-testid={`taxpal-chat-${m.role}`}>{md(m.content)}</div>
                  {m.role === "assistant" && !String(m.chat_id).startsWith("tmp") && (
                    <div className="flex gap-3 mt-1 px-1">
                      <button onClick={() => speak(m.chat_id)} className="text-[11px] text-[#A7B4CC] hover:text-white flex items-center gap-1" data-testid={`taxpal-speak-${m.chat_id}`}>
                        {speaking === m.chat_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Volume2 className="h-3 w-3" />} {speaking === m.chat_id ? "Playing…" : "Listen"}
                      </button>
                      <button onClick={() => handoff(m.chat_id)} disabled={!!m.handed_off_at || handing === m.chat_id} className="text-[11px] text-[#A7B4CC] hover:text-white flex items-center gap-1 disabled:opacity-60" data-testid={`taxpal-handoff-${m.chat_id}`}>
                        {handing === m.chat_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserCheck className="h-3 w-3" />} {m.handed_off_at ? "Sent to your tax pro" : "Ask my tax pro"}
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {busy && <div className="text-xs text-[#A7B4CC] flex items-center gap-2 px-2"><Loader2 className="h-3 w-3 animate-spin" /> Tax Pal is looking at your numbers…</div>}
              <div ref={endRef} />
            </div>
          )}
          {exhausted ? (
            <div className="bg-white/5 rounded-xl p-3 text-sm" data-testid="taxpal-chat-locked">
              <p className="font-semibold flex items-center gap-2"><Lock className="h-4 w-4 text-[#A7B4CC]" /> You've used your {state.limit} free questions this month</p>
              <p className="text-xs text-[#A7B4CC] mt-1">Every ScorpPath plan includes unlimited Tax Pal chat plus your own tax team.</p>
              <Button onClick={() => navigate("/#pricing")} variant="outline" data-testid="taxpal-chat-upgrade" className="mt-2 rounded-full border-[#7FE3AE]/60 text-[#7FE3AE] hover:bg-[#7FE3AE]/10 bg-transparent h-8 text-xs">See plans</Button>
            </div>
          ) : (
            <div className="flex gap-2">
              {canRecord && (
                <Button onClick={toggleRecord} disabled={busy} data-testid="taxpal-mic-btn" title={recording ? "Stop" : "Ask out loud"}
                  className={`rounded-full px-3 ${recording ? "bg-[#FCA5A5] text-[#7F1D1D] animate-pulse" : "bg-white/10 text-white hover:bg-white/20"}`}>
                  {recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </Button>
              )}
              <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder={recording ? "Listening… tap ■ when done" : "Type or tap the mic to ask out loud"}
                data-testid="taxpal-chat-input" className="rounded-full bg-white/10 border-white/20 text-white placeholder:text-white/40" />
              <Button onClick={() => ask()} disabled={busy || !input.trim()} data-testid="taxpal-chat-send" className="rounded-full bg-[#7FE3AE] text-[#02112A] hover:bg-[#9FF0C4] px-3">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          )}
          <p className="mt-2 text-[10px] text-[#A7B4CC] flex items-center gap-1"><Sparkles className="h-3 w-3" /> Answers use your vault numbers. Guidance only — your tax team confirms anything binding.</p>
        </div>
      )}
    </div>
  );
};

export default TaxPalChat;
